import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  Button,
  Alert,
  StyleSheet,
  TextInput,
  ActivityIndicator,
  ScrollView,
} from "react-native";
import { TSession } from "../../../../utils/types";
import {
  TIntegration,
  listIntegrations,
  deleteIntegration,
  updateIntegration,
  TCreateIntegrationRequest,
} from "../../../../services/api-integrations";
import {
  IntegrationType,
  TWebhookIntegrationConfig,
} from "../../../../utils/integrationTypes";

type Props = {
  session: TSession;
  onChange?: () => void;
};

const IntegrationManager: React.FC<Props> = ({ session, onChange }) => {
  const [integrations, setIntegrations] = useState<TIntegration[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const data = await listIntegrations(session);
      setIntegrations(data);
    } catch (error) {
      Alert.alert("Error", "Failed to fetch integrations.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (integrationId: number) => {
    const confirmDelete =
      typeof window !== "undefined"
        ? window.confirm("Are you sure you want to delete this integration?")
        : await new Promise<boolean>((resolve) =>
            Alert.alert(
              "Confirm",
              "Are you sure you want to delete this integration?",
              [
                {
                  text: "Cancel",
                  style: "cancel",
                  onPress: () => resolve(false),
                },
                {
                  text: "Delete",
                  style: "destructive",
                  onPress: () => resolve(true),
                },
              ],
            ),
          );

    if (!confirmDelete) return;

    try {
      setLoading(true);
      await deleteIntegration(session, integrationId);
      await fetchIntegrations();
      onChange?.();
    } catch (error) {
      Alert.alert("Error", "Failed to delete integration.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (integration: TIntegration) => {
    try {
      const updatedPayload: TCreateIntegrationRequest = {
        integration_type: integration.integration_type,
        credentials: integration.credentials ?? null,
        config: integration.config,
        is_active: !integration.is_active,
      };

      setLoading(true);
      await updateIntegration(
        session,
        integration.integration_id,
        updatedPayload,
      );
      await fetchIntegrations();
      onChange?.();
    } catch (error) {
      Alert.alert("Error", "Failed to update integration.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <Text style={styles.header}>🔍 Existing Integrations</Text>

      {loading ? (
        <ActivityIndicator size="large" color="#00B7F4" />
      ) : integrations.length === 0 ? (
        <Text style={styles.empty}>No integrations found.</Text>
      ) : (
        <ScrollView>
          {integrations.map((integration) => (
            <View key={integration.integration_id} style={styles.card}>
              <Text style={styles.cardTitle}>
                {integration.integration_type} (ID: {integration.integration_id}
                )
              </Text>
              <Text style={styles.cardDetail}>
                Active: {integration.is_active ? "✅" : "❌"}
              </Text>

              {integration.auto_sync_notes !== undefined && (
                <Text style={styles.cardDetail}>
                  Auto Sync Notes:{" "}
                  {integration.auto_sync_notes ? "Enabled" : "Disabled"}
                </Text>
              )}

              {integration.integration_type ===
              IntegrationType.GENERIC_WEBHOOK ? (
                <WebhookSummary
                  config={integration.config as TWebhookIntegrationConfig}
                />
              ) : (
                <>
                  <Text style={styles.cardDetail}>Credentials:</Text>
                  <TextInput
                    style={styles.input}
                    editable={false}
                    multiline
                    value={
                      integration.credentials
                        ? JSON.stringify(integration.credentials, null, 2)
                        : "None"
                    }
                  />

                  {integration.config && (
                    <>
                      <Text style={styles.cardDetail}>Config:</Text>
                      <TextInput
                        style={styles.input}
                        editable={false}
                        multiline
                        value={JSON.stringify(integration.config, null, 2)}
                      />
                    </>
                  )}
                </>
              )}

              <View style={styles.actions}>
                <Button
                  title={integration.is_active ? "Deactivate" : "Activate"}
                  onPress={() => handleToggleActive(integration)}
                  color="#28A745"
                />
                <Button
                  title="Delete"
                  onPress={() => handleDelete(integration.integration_id)}
                  color="#DC3545"
                />
              </View>
            </View>
          ))}
        </ScrollView>
      )}
    </View>
  );
};

type WebhookSummaryProps = {
  config?: TWebhookIntegrationConfig;
};

const WebhookSummary: React.FC<WebhookSummaryProps> = ({ config }) => {
  const fieldMappingsCount = config?.field_mappings?.length ?? 0;
  const headers = config?.headers ?? {};
  const headerKeys = Object.keys(headers);

  return (
    <View>
      <Text style={styles.cardDetail}>
        Destination URL: {config?.webhook_url ?? "Unknown"}
      </Text>
      <Text style={styles.cardDetail}>
        HTTP Method: {config?.http_method ?? "POST"}
      </Text>
      <Text style={styles.cardDetail}>
        Headers: {headerKeys.length ? headerKeys.join(", ") : "None"}
      </Text>
      <Text style={styles.cardDetail}>
        Field Mappings: {fieldMappingsCount}
      </Text>
      <Text style={styles.cardDetail}>Config JSON:</Text>
      <TextInput
        style={styles.input}
        editable={false}
        multiline
        value={JSON.stringify(config ?? {}, null, 2)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 12,
    backgroundColor: "#fff",
    borderRadius: 10,
    marginTop: 20,
  },
  header: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 16,
    textAlign: "center",
  },
  empty: {
    textAlign: "center",
    color: "#999",
  },
  card: {
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#dee2e6",
  },
  cardTitle: {
    fontWeight: "600",
    marginBottom: 8,
  },
  cardDetail: {
    fontSize: 14,
    marginBottom: 4,
  },
  input: {
    backgroundColor: "#fff",
    padding: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#ced4da",
    fontSize: 12,
    marginTop: 4,
    marginBottom: 8,
  },
  actions: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
  },
});

export default IntegrationManager;
